import { NgClass, NgStyle } from '@angular/common';
import { AfterViewInit, Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AvatarFullscreenHeaderSlot, DIdAvatarConfig } from '@shared/index';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormForBooleanComponent } from 'src/app/shared/components/ai-bot/utils/form-for-boolean/form-for-boolean.component';
import { SubmenuComponent } from 'src/app/shared/components/submenu/submenu.component';
import { SubmenuserviceService } from 'src/app/shared/components/submenu/submenuservice.service';
import { AvatarService, LiveAvatarEntry } from '@shared-angular/services/avatar.service';
import { drawChromaKeyedVideoFrame } from '@shared-angular/utils/chroma-key.util';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { HintComponent } from '@shared-angular/components/hint/hint.component';
import { CodemirrorCssComponent } from 'src/app/shared/components/ai-bot/utils/codemirror-css/codemirror-css.component';

@Component({
  imports: [NgClass, NgStyle,
    FormsModule,
    FormForBooleanComponent,
    HintComponent,
    SubmenuComponent,
    CodemirrorCssComponent,
    CardModule,
    InputTextModule,
    SelectButtonModule
  ],
  templateUrl: './did-avatar-tab.component.html',
  styleUrl: './did-avatar-tab.component.scss'
})
export class DIdAvatarTabComponent implements OnInit, AfterViewInit {
  public avatars: LiveAvatarEntry[] = [];
  public avatarAspectRatios = new Map<string, number>();
  private readonly CARD_SIZE = 200;

  // Mandanten-eigene Custom-Agents (ueber rag-admin dieser Org zugeordnet).
  public get myAvatars(): LiveAvatarEntry[] {
    return this.avatars.filter(a => a.avatarSource === 'customAgent' && !a.isGlobalDefault);
  }

  // Global über rag-admin kuratierte Standard-Avatare (fertig konfigurierte Custom-Agents, kein Expressive-Katalog mehr).
  public get standardAvatars(): LiveAvatarEntry[] {
    return this.avatars.filter(a => a.avatarSource === 'customAgent' && a.isGlobalDefault);
  }

  // Ob der aktuell gewaehlte Avatar ueberhaupt einen echten Greenscreen-Hintergrund hat.
  public get selectedAvatarIsGreenscreenCapable(): boolean {
    return this.avatars.find(a => a.id === this.config.avatarId)?.isGreenscreen ?? false;
  }

  // i18n
  public showPrompt = $localize`:@@show-prompt:Chat-Eingabezeile anzeigen`;
  public voiceInput = $localize`:@@voice-input:Spracheingabe`;
  public redirectChat = $localize`:@@redirect-chat:Wechseln zu Chat`;
  public redirectVoice = $localize`:@@redirect-voice:Wechseln zu Audio`;
  public cancelThinking = $localize`:@@cancel-thinking:Bot-Denken abbrechen`;
  public greenscreen = $localize`:@@greenscreen-mode:Greenscreen-Modus`;
  public enterCustomCss = $localize`:@@enter-custom-css:Gib deinen CSS-Code hier ein…`;

  constructor(
    private avatarService: AvatarService,
    public rss: RouteShareService,
    public submenuService: SubmenuserviceService
  ) { }

  public async ngOnInit(): Promise<void> {
    this.avatars = await this.avatarService.getDIdAvatars();
    // Seitenverhaeltnis nur fuer tatsaechlich angezeigte Custom-Agents ermitteln (nicht fuer den
    // ungenutzten Expressive-Katalog, der sonst unnoetig ~100 Thumbnails nachladen wuerde).
    this.avatars.filter(a => a.avatarSource === 'customAgent').forEach(avatar => {
      if (!avatar.previewUrl) return;
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          this.avatarAspectRatios.set(avatar.id, img.naturalWidth / img.naturalHeight);
        }
      };
      img.src = avatar.previewUrl;
    });
    this.ensureDIdAvatarConfig();
  }

  // Avatare, deren Greenscreen-Vorschau bereits freigestellt im Canvas liegt (Thumbnail wird dann ausgeblendet).
  public chromaKeyedIds = signal(new Set<string>());

  public async chromaKeyPreview(avatar: LiveAvatarEntry, canvas: HTMLCanvasElement): Promise<void> {
    if (!avatar.isGreenscreen || !avatar.idleVideoUrl || this.chromaKeyedIds().has(avatar.id)) return;
    if (await drawChromaKeyedVideoFrame(avatar.idleVideoUrl, canvas)) {
      this.chromaKeyedIds.update(ids => new Set(ids).add(avatar.id));
    }
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      const id = this.rss.botConfig.didAvatarConfig?.avatarId;
      if (!id) return;
      const isMyAvatar = this.myAvatars.some(a => a.id === id);
      const group = isMyAvatar ? this.myAvatars : this.standardAvatars;
      const index = group.findIndex(a => a.id === id) + 1;
      if (index > 0) {
        const gridSelector = isMyAvatar ? '.avatar-grid.my-avatars' : '.avatar-grid.catalog-avatars';
        document.querySelector(`${gridSelector} .avatar-card:nth-child(${String(index)})`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'center' });
      }
    }, 500);
  }

  private ensureDIdAvatarConfig(): void {
    this.rss.botConfig.didAvatarConfig ??= {
      avatarId: '',
      avatarName: '',
      avatarPreviewUrl: '',
      avatarIdleVideoUrl: '',
      backgroundUrl: '',
      useGreenscreen: false,
      promptbarActive: true,
      voiceInputActive: true,
      switchToChat: false,
      switchToVoice: false,
      cancelBotThinkingActive: false,
      fullscreenConfig: this.defaultFullscreenConfig()
    };
    // Bots, deren didAvatarConfig vor Einführung des Fullscreen-Modus angelegt wurde, besitzen das Feld noch nicht.
    this.rss.botConfig.didAvatarConfig.fullscreenConfig ??= this.defaultFullscreenConfig();
  }

  private defaultFullscreenConfig() {
    return {
      active: false,
      header: {
        left: { kind: 'none' as const, value: '' },
        center: { kind: 'none' as const, value: '' },
        right: { kind: 'none' as const, value: '' }
      }
    };
  }

  public get config(): DIdAvatarConfig {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.rss.botConfig.didAvatarConfig!;
  }

  public getInnerStyle(avatarId: string): Record<string, string> {
    const ratio = this.avatarAspectRatios.get(avatarId) ?? 1;
    let width: number;
    let height: number;
    if (ratio >= 1) {
      width = this.CARD_SIZE;
      height = Math.round(this.CARD_SIZE / ratio);
    } else {
      height = this.CARD_SIZE;
      width = Math.round(this.CARD_SIZE * ratio);
    }
    return { width: `${String(width)}px`, height: `${String(height)}px` };
  }

  public selectAvatar(avatar: LiveAvatarEntry): void {
    this.config.avatarId = avatar.id;
    this.config.avatarName = avatar.name;
    this.config.avatarPreviewUrl = avatar.previewUrl;
    this.config.avatarIdleVideoUrl = avatar.idleVideoUrl;
    // Greenscreen ist nur sinnvoll, wenn der neue Avatar tatsächlich einen gruenen Hintergrund hat.
    if (!avatar.isGreenscreen) {
      this.config.useGreenscreen = false;
    }
    void this.rss.saveBotConfigField('didAvatarConfig');
  }

  public saveConfig(): void {
    void this.rss.saveBotConfigField('didAvatarConfig');
  }

  public readonly headerSlotKindOptions: { label: string, value: AvatarFullscreenHeaderSlot['kind'] }[] = [
    { label: $localize`:@@fullscreen-header-slot-none:Kein Inhalt`, value: 'none' },
    { label: $localize`:@@fullscreen-header-slot-image:Bild`, value: 'image' },
    { label: $localize`:@@fullscreen-header-slot-text:Text`, value: 'text' }
  ];

  public fullscreenMode = $localize`:@@fullscreen-mode:Fullscreen-Modus`;

  // Im Fullscreen-Modus ist Greenscreen zwingend erforderlich (siehe Bot-seitiges effectiveGreenscreen) -
  // useGreenscreen wird beim Aktivieren daher konsistent mitgesetzt, auch wenn der Toggle selbst disabled ist.
  public toggleFullscreen(): void {
    if (this.config.fullscreenConfig?.active) {
      this.config.useGreenscreen = true;
    }
    this.saveConfig();
  }
}
